import React, { useState, useContext, useEffect } from "react"
import BlockContent from "@sanity/block-content-to-react"
import Ratings from "react-ratings-declarative"
import AnchorLink from "react-anchor-link-smooth-scroll"
import { StoreContext } from "../../context/store-context"
import { bgColorResolver, colorResolver, borderColorResolver } from "../../util"
import {
  VeraImageSquare,
  VeraImageWide,
  VeraImage,
} from "../../components/elements/vera-image"
import HorizontalScrollable from "../../components/hoc/horizontal-scrollable"
import Article from "../../components/dynamic-components/article/article"
import { Link } from "gatsby"
import BreadCrumb from "../elements/breadcrumb"
import SectionColumns from "../elements/section-columns"
import Review from "./review/review"

const ProductPageComponents = ({ data }) => {
  const {
    product_variants,
    product_name,
    _id,
    product_preview_color,
    product_image,
    product_intro_text,
    image_rotate,
    product_flavor_profile,
    product_collection,
    _rawProductDescription,
    _rawProductHowToServe,
    product_article_teaser,
    informative_label,
    ingredients,
    product_meta_data,
    allergens,
    slug,
    product_label,
    product_disabled,
  } = data.sanityProduct
  const bgColorClass = bgColorResolver(product_preview_color.section_background)
  const colorClass = colorResolver(product_preview_color.section_background)
  const product_related_product = product_meta_data
    ? product_meta_data.product_related_product
    : []
  const [variant, setVariant] = useState(0)
  const [reviews, setReviews] = useState([])
  const [reviewsLoading, setReviewsLoading] = useState(true)
  const [reviewsError, setReviewsError] = useState(false)
  const storeContext = useContext(StoreContext)
  const menuItems = [
    { title: "Flavor Profile", anchor: "#profile" },
    { title: "Inspiration", anchor: "#inspiration" },
    { title: "Product Information", anchor: "#info" },
  ]

  const inCart = storeContext.cart
    .filter(item => item._id === _id)
    .filter(
      item => item.variant._key === product_variants.variants[variant]._key
    )

  const removeFromCart = e => {
    e.preventDefault()
    const item = {
      _id,
      product_name,
      variant: product_variants.variants[variant],
    }
    storeContext.removeFromCart(item)
  }

  const addToCart = e => {
    e.preventDefault()
    const item = {
      link: `/shop/${product_collection.slug.current}/${slug.current}/`,
      _id,
      product_image,
      product_name,
      variant: product_variants.variants[variant],
      variant_type: product_variants.variant_type,
    }
    storeContext.addToCart(item)
  }

  const profileScore = (title, score) => {
    const items = []
    for (let i = 0; i < 6; i++) {
      if (i < score)
        items.push(
          <span key={`${title}-${i}`} className="product-info__score--filled" />
        )
      else {
        items.push(
          <span key={`${title}-${i}`} className="product-info__score--empty" />
        )
      }
    }
    return items
  }

  const labels = () => {
    const labelEl = product_label.map(label => {
      return (
        <span
          className={`${bgColorResolver(
            label.label_color.section_background
          )} ${colorResolver(
            label.label_color.section_background
          )} product-page__label`}
        >
          {label.label_title}
        </span>
      )
    })
    return (
      <div className="product-page__label-wrapper">
        <div className="product-page__label-controller">{labelEl}</div>
      </div>
    )
  }

  useEffect(() => {
    const getComments = async () => {
      const response = await fetch(
        `${process.env.GATSBY_API_BASE_URL}api/comment?productId=${_id}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      )
      if (response.status !== 200) {
        setReviewsLoading(false)
        setReviewsError(true)
        return
      }
      const reviews = await response.json()
      setReviews(reviews)
      setReviewsLoading(false)
    }
    if (reviews.length === 0) getComments()
  }, [])
  const totalScore = reviews.reduce((a, b) => a + b.rating, 0) / reviews.length

  return (
    <>
      <section className="section has-padding-top-0--mobile has-padding-none--tablet has-padding-top-1--tablet">
        <div className="columns is-gapless">
          <div className={`column has-no-margin ${bgColorClass}`}>
            {labels()}
            <div className="product-page__image-wrapper">
              <VeraImageSquare
                className={`${
                  image_rotate
                    ? "product-page__image-wrapper--rotate"
                    : "product-page__image-wrapper--standard"
                } is-hidden-tablet`}
                asset={product_image.asset}
                contain
              />
              <VeraImageWide
                className={`${
                  image_rotate
                    ? "product-page__image-wrapper--rotate"
                    : "product-page__image-wrapper--standard"
                } is-hidden-mobile`}
                asset={product_image.asset}
                contain
              />
            </div>
          </div>
          <div className="column">
            <div className="has-background-light product-info" id="profile">
              <div className="product-page__location is-hidden-tablet">
                <p
                  className={colorClass}
                  style={{ opacity: 0.7, fontSize: "0.875rem" }}
                >
                  You are here:{" "}
                  <span style={{ textTransform: "uppercase" }}>
                    {product_collection.collection_title}
                  </span>
                </p>
              </div>
              <div
                className="is-hidden-desktop"
                style={{ paddingBottom: 10, paddingTop: 10 }}
              >
                {reviews.length > 0 ? (
                  <>
                    <Ratings rating={totalScore} widgetSpacings="5px">
                      <Ratings.Widget
                        widgetDimension="20px"
                        widgetRatedColor="#1a1a38"
                      />
                      <Ratings.Widget
                        widgetDimension="20px"
                        widgetRatedColor="#1a1a38"
                      />
                      <Ratings.Widget
                        widgetDimension="20px"
                        widgetRatedColor="#1a1a38"
                      />
                      <Ratings.Widget
                        widgetDimension="20px"
                        widgetRatedColor="#1a1a38"
                      />
                      <Ratings.Widget
                        widgetDimension="20px"
                        widgetRatedColor="#1a1a38"
                      />
                    </Ratings>
                    <span
                      style={{
                        fontSize: "0.875rem",
                        marginLeft: 10,
                        textDecoration: "underline",
                      }}
                    >
                      <AnchorLink offset="100" href="#reviews">
                        Read reviews
                      </AnchorLink>
                    </span>
                  </>
                ) : (
                  <div style={{ height: 24 }}></div>
                )}
              </div>

              <div>
                <div className="is-hidden-touch" style={{ paddingLeft: 1 }}>
                  {reviews.length > 0 ? (
                    <>
                      <Ratings rating={totalScore} widgetSpacings="5px">
                        <Ratings.Widget
                          widgetDimension="25px"
                          widgetRatedColor="#1a1a38"
                        />
                        <Ratings.Widget
                          widgetDimension="25px"
                          widgetRatedColor="#1a1a38"
                        />
                        <Ratings.Widget
                          widgetDimension="25px"
                          widgetRatedColor="#1a1a38"
                        />
                        <Ratings.Widget
                          widgetDimension="25px"
                          widgetRatedColor="#1a1a38"
                        />
                        <Ratings.Widget
                          widgetDimension="25px"
                          widgetRatedColor="#1a1a38"
                        />
                      </Ratings>
                      <span
                        style={{
                          fontSize: "1rem",
                          marginLeft: 10,
                          textDecoration: "underline",
                        }}
                      >
                        <AnchorLink offset="100" href="#reviews">
                          Read reviews
                        </AnchorLink>
                      </span>
                    </>
                  ) : (
                    <div style={{ height: 24 }}></div>
                  )}
                </div>
                <h1 className="product-info__title">{product_name}</h1>
                {product_intro_text && (
                  <p className="product-info__intro-text">
                    {product_intro_text}
                  </p>
                )}
              </div>
              {product_variants.variant_type && (
                <div className="product-info__variant">
                  <span>{product_variants.variant_type}:</span>
                  {product_variants.variants.map((currVariant, i) => (
                    <button
                      className={`product-info__variant-item ${i === variant &&
                        bgColorClass} ${i === variant && colorClass}`}
                      key={currVariant.variant_info_value}
                      onClick={() => setVariant(i)}
                    >
                      {currVariant.variant_info_value}
                    </button>
                  ))}
                </div>
              )}

              <div className="columns">
                <div className="column is-8">
                  <div className="columns is-multiline is-gapless">
                    {product_flavor_profile &&
                      product_flavor_profile.score &&
                      product_flavor_profile.score.length > 0 && (
                        <>
                          <div
                            style={{ marginBottom: 10 }}
                            className="column is-12 has-text-dark"
                          >
                            Flavor profile
                          </div>
                          {product_flavor_profile.score.map(item => (
                            <div
                              style={{ marginBottom: 10 }}
                              key={item.title_ref.title}
                              className="column is-full is-half-fullhd flavor-profile"
                            >
                              <span className="product-info__profile-title">
                                {item.title_ref.title}
                              </span>
                              <span className="product-info__score">
                                {profileScore(item.title_ref.title, item.score)}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                  </div>
                </div>
                <div
                  className="column is-4 is-hidden-mobile"
                  style={{ paddingRight: "3.5%" }}
                >
                  <div
                    className="product-info__price has-text-dark"
                    style={{ paddingBottom: 10 }}
                  >
                    {product_variants.variants[variant].variant_info_price} DKK
                  </div>
                </div>
              </div>
              <div
                className={`product-page__desktop-add ${
                  inCart.length > 0
                    ? `${borderColorResolver(
                        product_preview_color.section_background
                      )} active-amount`
                    : ""
                }`}
              >
                {inCart.length > 0 ? (
                  <>
                    <button
                      className={`shop-btn ${bgColorClass} ${colorClass}`}
                      onClick={removeFromCart}
                    >
                      <i className="icon-minus" />
                    </button>
                    <span className="amount-btn">{inCart.length}</span>
                    <button
                      className={`shop-btn ${bgColorClass} ${colorClass}`}
                      onClick={addToCart}
                    >
                      <i className="icon-plus" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={addToCart}
                    disabled={product_disabled}
                    className={`product-page__desktop-add-button ${bgColorClass} ${colorClass}`}
                  >
                    Add to cart
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="section has-padding-top-0--mobile">
        <div className="container">
          <div className="columns">
            <div className="column">
              <HorizontalScrollable
                className="product-page__menu"
                childClassName="product-page__menu-item"
              >
                {menuItems.map(item => {
                  if (item.anchor === "#profile") {
                    if (
                      !product_flavor_profile ||
                      !product_flavor_profile.score ||
                      !product_flavor_profile.score.length > 0
                    ) {
                      return null
                    }
                  } else if (item.anchor === "#inspiration") {
                    if (
                      !product_article_teaser ||
                      !product_article_teaser.references ||
                      !product_article_teaser.references.length > 0
                    ) {
                      return null
                    }
                  }
                  return (
                    <AnchorLink
                      offset="100"
                      className="anchor-link"
                      key={item.anchor}
                      href={item.anchor}
                    >
                      {item.title}
                    </AnchorLink>
                  )
                })}
              </HorizontalScrollable>
            </div>
          </div>
        </div>
      </section>
      <section className="section product-description has-padding-top-0--mobile">
        <div className="container">
          <div className="columns">
            <div className="column is-2 is-hidden-mobile">
              <BreadCrumb
                section={<Link to="/shop/">Shop</Link>}
                title={product_collection.collection_title}
              />
            </div>
            <div className="column is-8 rich-text">
              <div className="columns">
                <div className="column product-description__divider-product">
                  <p>
                    <strong>Product Description</strong>
                  </p>
                  {_rawProductDescription ? (
                    <BlockContent blocks={_rawProductDescription} />
                  ) : (
                    <p>Stay tuned...</p>
                  )}
                </div>
                <div className="column product-description__divider-suggestion">
                  <p>
                    <strong>Serving Suggestion</strong>
                  </p>
                  {_rawProductHowToServe ? (
                    <BlockContent blocks={_rawProductHowToServe} />
                  ) : (
                    <p>Stay tuned...</p>
                  )}
                </div>
              </div>
            </div>
            <div className="column is-2 is-hidden-mobile"></div>
          </div>
        </div>
      </section>
      {product_article_teaser &&
        product_article_teaser.references &&
        product_article_teaser.references.length > 0 && (
          <section
            id="inspiration"
            className="section product-articles has-padding-top-0--mobile"
          >
            <div className="container">
              <div className="columns">
                <div className="column is-8 is-offset-2">
                  <div className="columns">
                    <div className="column has-text-centered">
                      <h2 className="h3">
                        {product_article_teaser.title
                          ? product_article_teaser.title
                          : "Inspiration"}
                      </h2>
                      {product_article_teaser.intro_text && (
                        <p className="sub-header product-articles__intro-text">
                          {product_article_teaser.intro_text}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="columns">
                    {product_article_teaser.references.map(
                      article =>
                        article && (
                          <div key={article._id} className="column">
                            <Article {...article} />
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      {reviewsError ? (
        <></>
      ) : (
        <SectionColumns id="reviews">
          <div className="column is-8 is-offset-2">
            {reviewsLoading ? (
              <div className="loader is-loading"></div>
            ) : (
              <Review
                productId={_id}
                reviews={reviews}
                setReviews={setReviews}
              />
            )}
          </div>
        </SectionColumns>
      )}
      <section id="info" className="section informative-label">
        <div className="container">
          <div className="columns">
            <div className="column is-8 is-offset-2">
              <div className="columns is-multiline">
                <div className="column is-12 is-half-widescreen">
                  <div className="informative-label__label rich-text">
                    <p>
                      <strong>Nutritional Information (per 100 grams)</strong>
                    </p>
                    <table className="table is-fullwidth">
                      <tbody>
                        {informative_label.map((label, i) => (
                          <tr key={i}>
                            <td>{label.key}</td>
                            <td>{label.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="column">
                  <div className="informative-label__info rich-text">
                    <p>
                      <strong>Ingredients</strong>
                      <br />
                      {ingredients}
                    </p>
                    <p>
                      <strong>Refrigerate?</strong>
                      <br />
                      {product_meta_data && (
                        <>{product_meta_data.refrigerate ? "Yes" : "No"}</>
                      )}
                    </p>
                    {allergens && (
                      <p>
                        <strong>Allergens</strong>
                        <br />
                        {allergens}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {product_related_product && product_related_product.length > 0 && (
        <section className="section has-background-light">
          <div className="container">
            <div className="columns" style={{ marginBottom: "2rem" }}>
              <div className="column has-text-centered">
                <h2 className="h3">Related Products</h2>
              </div>
            </div>
            <div className="columns is-multiline is-mobile is-centered">
              {product_related_product.map(
                (product, i) =>
                  product && (
                    <div
                      key={`${product.title}_${i}`}
                      className="column is-half-mobile is-one-quarter-tablet"
                    >
                      <Link
                        to={`/shop/${product.product_collection.slug.current}/${product.slug.current}`}
                      >
                        <div className="product">
                          <div className="product__image-wrapper">
                            <VeraImage
                              asset={product.product_image.asset}
                              contain
                              className="transform-animate"
                            />
                          </div>
                          <div className="product__title has-text-centered">
                            {product.product_name}
                          </div>
                        </div>
                      </Link>
                    </div>
                  )
              )}
            </div>
          </div>
        </section>
      )}
      <div className="product-page__mobile-add">
        <span className="product-page__mobile-add-pricetag">
          {product_variants.variants[variant].variant_info_price} DKK
        </span>
        <button
          onClick={addToCart}
          disabled={product_disabled}
          className={`product-page__mobile-add-button ${bgColorClass} ${colorClass}`}
        >
          Add to cart
        </button>
      </div>
    </>
  )
}

export default ProductPageComponents
